import { forwardRef, type ComponentPropsWithoutRef, type ElementRef } from 'react';
import * as RadixSwitch from '@radix-ui/react-switch';
import { cn } from '@/lib/cn';

export type SwitchProps = ComponentPropsWithoutRef<typeof RadixSwitch.Root>;

export const Switch = forwardRef<ElementRef<typeof RadixSwitch.Root>, SwitchProps>(function Switch(
  { className, ...rest },
  ref,
) {
  return (
    <RadixSwitch.Root
      ref={ref}
      className={cn(
        'relative inline-flex h-[22px] w-[38px] shrink-0 cursor-pointer items-center rounded-full',
        // Invisible hit-area extension to the 44px floor on touch (22+2×12 tall,
        // 38+2×4 wide) — the visible track stays 22×38.
        "pointer-coarse:before:absolute pointer-coarse:before:-inset-x-1 pointer-coarse:before:-inset-y-3 pointer-coarse:before:content-['']",
        'bg-rail transition-colors duration-press ease-out-strong',
        'data-[state=checked]:bg-brand',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand focus-visible:ring-offset-2 focus-visible:ring-offset-canvas',
        'disabled:cursor-not-allowed disabled:opacity-50',
        className,
      )}
      {...rest}
    >
      <RadixSwitch.Thumb
        className={cn(
          // Hairline border, not a shadow — the paper idiom for separation.
          'pointer-events-none block h-[18px] w-[18px] translate-x-0.5 rounded-full border border-line-2 bg-card',
          'transition-transform duration-press ease-out-strong',
          'data-[state=checked]:translate-x-[18px]',
          'motion-reduce:transition-none',
        )}
      />
    </RadixSwitch.Root>
  );
});
